import { AxiosInstance, AxiosResponse } from "axios";
import {
  WfsResponseType,
  WfsDataResponse,
  WfsDeferredRequestResponse,
  WfsPaymentRequiredResponse,
  WfsBadRequestResponse,
  WfsDuplicateOrderResponse,
} from "../types/Wfs/WfsResponse";
import { DeferredRequestProgressResponse } from "../types/Wfs/DeferredRequestProgressResponse";
import {
  WfsCapabilitiesResponse,
  WfsFeatureType,
} from "../types/Wfs/WfsCapabilities";
import * as jxon from "jxon";

export default class WfsService {
  private _endpoint = "wfs";
  private _lbBaseUrl = process.env.REACT_APP_LB_URL;
  private _client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this._client = client;
  }

  public async WfsPost<WfsGetFeatureRequestType>(
    params: WfsGetFeatureRequestType,
  ): Promise<
    | WfsDataResponse
    | WfsDeferredRequestResponse
    | WfsPaymentRequiredResponse
    | WfsBadRequestResponse
    | WfsDuplicateOrderResponse
  > {
    try {
      var response: AxiosResponse<Blob> = await this._client.post(
        `${this._endpoint}`,
        params,
        {
          baseURL: this._lbBaseUrl,
          timeout: 0,
          //@ts-ignore - suppressAlert isn't defined on the Axios Config interface
          suppressAlert: true,
          responseType: "blob",
        },
      );
      switch (response.status) {
        case 200:
          return this.HandleWfsDataResponse(response);
        case 202:
          return await this.HandleWfsDeferredRequestResponse(response);
        default:
          throw new Error("Unknown response type");
      }
    } catch (error: any) {
      switch (error.response?.status) {
        case 400:
        case 500:
          return await this.HandleWfsBadRequestResponse(error.response);
        case 402:
          return await this.HandleWfsPaymentRequiredResponse(error.response);
        case 409:
          return await this.HandleWfsDuplicateOrderResponse(error.response);
        default:
          throw error;
      }
    }
  }

  public async PollDeferredRequest(
    id: string,
  ): Promise<DeferredRequestProgressResponse> {
    return this._client
      .get<DeferredRequestProgressResponse>(`${this._endpoint}/deferred/${id}`)
      .then((response) => {
        return response.data;
      });
  }

  public async WfsGetFeatureTypes(): Promise<WfsFeatureType[]> {
    return this._client
      .get(`${this._endpoint}`, {
        params: {
          service: "WFS",
          version: "2.0.0",
          request: "GetCapabilities",
          section: "FeatureTypeList",
        },
      })
      .then((response) => {
        jxon.config({
          attrPrefix: "",
          valueKey: "value",
          parseValues: true,
        });
        const wfsCapabilitiesResponse = jxon.stringToJs(
          response.data,
        ) as WfsCapabilitiesResponse;
        const featureTypes =
          wfsCapabilitiesResponse["wfs:WFS_Capabilities"][
            "wfs:FeatureTypeList"
          ]?.["wfs:FeatureType"];
        if (featureTypes === undefined)
          throw new Error("GetCapabilities did not return the FeatureTypeList");
        return featureTypes;
      });
  }

  private HandleWfsDataResponse(
    response: AxiosResponse<Blob>,
  ): WfsDataResponse {
    const fileName = this.GetFilename(response);
    return {
      type: WfsResponseType.Data,
      data: new Blob([response.data]),
      fileName: fileName,
    };
  }

  private async HandleWfsDeferredRequestResponse(
    response: AxiosResponse<any>,
  ): Promise<WfsDeferredRequestResponse> {
    const data = await this.ReadDataFromBlob(response.data);
    return {
      type: WfsResponseType.DeferredRequest,
      deferredRequestId: data.requestId,
    };
  }

  private async HandleWfsBadRequestResponse(
    response: AxiosResponse<Blob>,
  ): Promise<WfsBadRequestResponse> {
    const data = await this.ReadDataFromBlob(response.data);
    return {
      type: WfsResponseType.BadRequest,
      error:
        typeof data === "string"
          ? data
          : "Something went wrong with your request",
    };
  }

  private async HandleWfsDuplicateOrderResponse(
    response: AxiosResponse<Blob>,
  ): Promise<WfsDuplicateOrderResponse> {
    const data = await this.ReadDataFromBlob(response.data);
    return {
      type: WfsResponseType.DuplicateOrder,
      orderId: data.orderId,
      orderItemId: data.orderItemId,
      isInBasket: data.isInBasket,
    };
  }

  private async HandleWfsPaymentRequiredResponse(
    response: AxiosResponse<any, any>,
  ): Promise<WfsPaymentRequiredResponse> {
    const data = await this.ReadDataFromBlob(response.data);
    return {
      type: WfsResponseType.PaymentRequired,
      id: data.id,
      description: data.description,
      layerName: data.layerName,
      priceExTax: data.priceExTax,
      priceIncTax: data.priceIncTax,
      transactionCurrencyCode: data.transactionCurrencyCode,
      taxCode: data.taxCode,
      isPremium: data.isPremium,
      parameters: data.parameters,
    };
  }

  private GetFilename(response: AxiosResponse) {
    const contentDisposition = response.headers["content-disposition"];
    if (contentDisposition !== null) {
      const pattern = new RegExp("filename=([^;$]*)");
      const match = pattern.exec(response.headers["content-disposition"]);
      if (match && match.length >= 2) return match[1];
    }

    throw new Error("Could not get filename");
  }

  private async ReadDataFromBlob(data: Blob): Promise<any> {
    const blobText = await data.text();
    try {
      const object = JSON.parse(blobText);
      return object;
    } catch {
      return blobText;
    }
  }
}
